import { useState, useEffect, useRef } from 'react'
import { formatSalaryInput } from '../../utils/formatCurrency'
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Grid,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import {
  Work as WorkIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import { Client, clientsApi } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import DiscardChangesDialog from '../common/DiscardChangesDialog'
import DreamJobSummary from './DreamJobSummary'
import {
  CompanyTier,
  DreamJobFormData,
  TIER_ORDER,
  Urgency,
  WorkStyle,
  dreamJobToForm,
  emptyDreamJobForm,
  mergeDreamJob,
  tierGroupLabels,
  urgencyLabels,
  workStyleLabels,
} from '../../utils/dreamJobForm'

interface DreamJobFormProps {
  client: Client
  onUpdate: (client: Partial<Client>) => void
  /** Reports whether the form holds edits nothing has saved, so a parent that
      can unmount this form (a tab switch) can ask before discarding them. */
  onDirtyChange?: (dirty: boolean) => void
}

const defaultBenefits = [
  'Health Insurance',
  '401k Match',
  'Stock Options',
  'Unlimited PTO',
  'Remote Work',
  'Professional Development',
  'Flexible Hours',
  'Parental Leave',
]

const defaultPriorities = [
  'Work-Life Balance',
  'Career Growth',
  'Compensation',
  'Company Culture',
  'Job Security',
  'Learning Opportunities',
  'Team Quality',
  'Impact/Mission',
]

// A selectable chip whose "picked" state never rests on color alone: a
// filled vs. outlined treatment, a leading check mark, and a heavier label
// weight all move together. Fixes the bug where a chip that IS one of the
// client's actual selections was visually identical to one that wasn't.
function SelectableChip({
  label,
  selected,
  onClick,
}: {
  label: string
  selected: boolean
  onClick: () => void
}) {
  return (
    <Chip
      label={label}
      onClick={onClick}
      icon={selected ? <CheckIcon /> : undefined}
      color={selected ? 'primary' : 'default'}
      variant={selected ? 'filled' : 'outlined'}
      aria-pressed={selected}
      sx={{ fontWeight: selected ? 700 : 500 }}
    />
  )
}

type FormData = DreamJobFormData

const emptyForm = emptyDreamJobForm

export default function DreamJobForm({ client, onUpdate, onDirtyChange }: DreamJobFormProps) {
  const { showSuccess, showError } = useNotification()
  const hasSavedProfile = !!client.dreamJob
  // A saved profile opens as a read-only summary; one nobody has saved yet has
  // nothing to read, so it opens ready to fill in.
  const [mode, setMode] = useState<'view' | 'edit'>(hasSavedProfile ? 'view' : 'edit')
  const [formData, setFormData] = useState<FormData>(() =>
    client.dreamJob ? dreamJobToForm(client.dreamJob) : emptyForm
  )
  const [saving, setSaving] = useState(false)
  const [discardOpen, setDiscardOpen] = useState(false)
  // The last payload known to be on the server. Everything else about saving
  // is derived from it: the form is dirty when it disagrees with this.
  const savedRef = useRef<string | null>(null)
  if (savedRef.current === null && client.dreamJob) savedRef.current = JSON.stringify(formData)
  const formDataRef = useRef<FormData>(emptyForm)
  const savingRef = useRef(false)
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const targetRoleRef = useRef<HTMLInputElement>(null)
  formDataRef.current = formData
  const [newCompany, setNewCompany] = useState('')
  const [newCompanyTier, setNewCompanyTier] = useState<CompanyTier>('goal')
  const [newLocation, setNewLocation] = useState('')
  const [newDealBreaker, setNewDealBreaker] = useState('')
  const [newJobLink, setNewJobLink] = useState('')
  const [jobLinkError, setJobLinkError] = useState<string | null>(null)

  // Validates a Target Job Link before it's accepted as a chip. Returns the
  // normalized URL (with https:// prepended if missing) or an error message.
  // Catches the previously-reported bug where strings like "not a url" or
  // "foo" would get https:// auto-prepended into a bogus URL and accepted.
  const validateJobLink = (raw: string): { url?: string; error?: string } => {
    const trimmed = raw.trim()
    if (!trimmed) return { error: 'Please enter a URL' }
    if (/\s/.test(trimmed)) return { error: 'URL cannot contain spaces' }
    const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
    let parsed: URL
    try {
      parsed = new URL(candidate)
    } catch {
      return { error: 'Please enter a valid URL (e.g., https://linkedin.com/jobs/12345)' }
    }
    if (!parsed.hostname.includes('.')) {
      return { error: 'Please enter a valid URL with a domain (e.g., linkedin.com)' }
    }
    return { url: candidate }
  }

  const isComplete = !!client.dreamJobCompletedAt
  const dirty =
    mode === 'edit' && savedRef.current !== null && savedRef.current !== JSON.stringify(formData)

  useEffect(() => {
    if (!client.dreamJob) {
      // Nothing saved yet, so anything typed from here is already a change.
      if (savedRef.current === null) savedRef.current = JSON.stringify(emptyForm)
      return
    }
    const next = dreamJobToForm(client.dreamJob)
    // update-client broadcasts client_updated to every open session, so this
    // fires while someone is mid-sentence — including right after this form's
    // own save. Re-syncing then would overwrite the field under their cursor,
    // so the server's copy is taken only when nothing local is unsaved.
    const isDirty = savedRef.current !== JSON.stringify(formDataRef.current)
    if (savedRef.current !== null && isDirty) return
    setFormData(next)
    savedRef.current = JSON.stringify(next)
  }, [client.dreamJob])

  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])

  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])

  // Closing or reloading the tab is the one exit the app cannot intercept with
  // its own dialog, so the browser's prompt stands in for it.
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  // Focus follows the mode so a keyboard user is not left on a button that has
  // just disappeared.
  const previousMode = useRef(mode)
  useEffect(() => {
    if (previousMode.current === mode) return
    previousMode.current = mode
    if (mode === 'edit') targetRoleRef.current?.focus()
    else editButtonRef.current?.focus()
  }, [mode])

  const resetEntryFields = () => {
    setNewCompany('')
    setNewLocation('')
    setNewDealBreaker('')
    setNewJobLink('')
    setJobLinkError(null)
  }

  // Saving replaces the whole dream job, and the form can have been open for
  // minutes, so the current server copy is read first and anything changed
  // elsewhere in the meantime (the Target Companies tab, another session) is
  // kept rather than overwritten.
  const handleSave = async () => {
    const payload = formDataRef.current
    if (!payload.targetRole) {
      showError('Please enter a target role')
      return
    }
    if (savingRef.current) return
    savingRef.current = true
    setSaving(true)

    try {
      let toSave = payload
      let tookRemote = false
      if (savedRef.current !== null) {
        const latest = await clientsApi.get(client.id)
        if (latest.dreamJob) {
          const result = mergeDreamJob(
            JSON.parse(savedRef.current) as FormData,
            payload,
            dreamJobToForm(latest.dreamJob)
          )
          toSave = result.merged
          tookRemote = result.tookRemote
        }
      }
      const result = await clientsApi.saveDreamJob(client.id, toSave)
      savedRef.current = JSON.stringify(toSave)
      setFormData(toSave)
      onUpdate({
        dreamJob: result.dreamJob,
        dreamJobCompletedAt: new Date().toISOString(),
      })
      resetEntryFields()
      setMode('view')
      showSuccess(
        tookRemote
          ? 'Dream job profile saved, along with newer changes made elsewhere'
          : 'Dream job profile saved'
      )
    } catch (err) {
      console.error('Error saving dream job:', err)
      // What they typed stays in the fields so nothing is lost; they can retry.
      showError(err instanceof Error ? err.message : 'Failed to save dream job profile')
    } finally {
      savingRef.current = false
      setSaving(false)
    }
  }

  const leaveEdit = () => {
    resetEntryFields()
    setMode('view')
  }

  const handleCancel = () => {
    if (dirty) setDiscardOpen(true)
    else leaveEdit()
  }

  const handleDiscard = () => {
    if (savedRef.current !== null) setFormData(JSON.parse(savedRef.current) as FormData)
    setDiscardOpen(false)
    leaveEdit()
  }

  const addCompany = () => {
    if (newCompany.trim() && !formData.targetCompanies.some((c) => c.name.toLowerCase() === newCompany.trim().toLowerCase())) {
      setFormData({ ...formData, targetCompanies: [...formData.targetCompanies, { name: newCompany.trim(), tier: newCompanyTier }] })
      setNewCompany('')
    }
  }

  const removeCompany = (companyName: string) => {
    setFormData({ ...formData, targetCompanies: formData.targetCompanies.filter((c) => c.name !== companyName) })
  }

  const addJobLink = () => {
    const result = validateJobLink(newJobLink)
    if (result.error || !result.url) {
      setJobLinkError(result.error ?? 'Please enter a valid URL')
      return
    }
    if (!formData.targetJobLinks.includes(result.url)) {
      setFormData({ ...formData, targetJobLinks: [...formData.targetJobLinks, result.url] })
    }
    setNewJobLink('')
    setJobLinkError(null)
  }

  const removeJobLink = (link: string) => {
    setFormData({ ...formData, targetJobLinks: formData.targetJobLinks.filter((l) => l !== link) })
  }

  const addLocation = () => {
    if (newLocation.trim() && !formData.locationPreferences.includes(newLocation.trim())) {
      setFormData({ ...formData, locationPreferences: [...formData.locationPreferences, newLocation.trim()] })
      setNewLocation('')
    }
  }

  const removeLocation = (location: string) => {
    setFormData({ ...formData, locationPreferences: formData.locationPreferences.filter((l) => l !== location) })
  }

  const addDealBreaker = () => {
    if (newDealBreaker.trim() && !formData.dealBreakers.includes(newDealBreaker.trim())) {
      setFormData({ ...formData, dealBreakers: [...formData.dealBreakers, newDealBreaker.trim()] })
      setNewDealBreaker('')
    }
  }

  const removeDealBreaker = (item: string) => {
    setFormData({ ...formData, dealBreakers: formData.dealBreakers.filter((d) => d !== item) })
  }

  const toggleBenefit = (benefit: string) => {
    if (formData.benefitsPriorities.includes(benefit)) {
      setFormData({ ...formData, benefitsPriorities: formData.benefitsPriorities.filter((b) => b !== benefit) })
    } else {
      setFormData({ ...formData, benefitsPriorities: [...formData.benefitsPriorities, benefit] })
    }
  }

  const togglePriority = (priority: string) => {
    if (formData.priorities.includes(priority)) {
      setFormData({ ...formData, priorities: formData.priorities.filter((p) => p !== priority) })
    } else {
      setFormData({ ...formData, priorities: [...formData.priorities, priority] })
    }
  }

  // The chip catalog is a fixed list, but a client's actual selection can
  // include a value outside it (free text carried over from a migration, or
  // typed before the catalog existed). Rendering only the fixed list would
  // silently drop those from view even though they are the client's real
  // answer — the catalog is unioned with whatever is actually selected so
  // nothing selected ever goes unrendered.
  const benefitOptions = Array.from(new Set([...defaultBenefits, ...formData.benefitsPriorities]))
  const priorityOptions = Array.from(new Set([...defaultPriorities, ...formData.priorities]))

  const companiesByTier = TIER_ORDER.map((tier) => ({
    tier,
    companies: formData.targetCompanies.filter((c) => c.tier === tier),
  })).filter((group) => group.companies.length > 0)

  const statusAlert = (
    <Alert
      severity={isComplete ? 'success' : 'info'}
      icon={isComplete ? <CheckCircleIcon /> : <WorkIcon />}
      sx={{ flex: 1 }}
    >
      {isComplete
        ? 'Dream job profile is complete. You can still update it if needed.'
        : 'Define the ideal job profile to guide the job search process.'}
    </Alert>
  )

  if (mode === 'view') {
    return (
      <Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { sm: 'center' },
            gap: 2,
            mb: 3,
          }}
        >
          {statusAlert}
          <Button
            ref={editButtonRef}
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => setMode('edit')}
            sx={{ flexShrink: 0 }}
          >
            Edit
          </Button>
        </Box>
        <DreamJobSummary data={formData} />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', mb: 3 }}>{statusAlert}</Box>

      {/* Target Role Section */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Target Position
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Target Role"
              inputRef={targetRoleRef}
              value={formData.targetRole}
              onChange={(e) => setFormData({ ...formData, targetRole: e.target.value })}
              placeholder="e.g., Senior Product Manager"
              required
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Target Industry"
              value={formData.targetIndustry}
              onChange={(e) => setFormData({ ...formData, targetIndustry: e.target.value })}
              placeholder="e.g., Technology, Healthcare"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Target Companies
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <TextField
                size="small"
                value={newCompany}
                onChange={(e) => setNewCompany(e.target.value)}
                placeholder="Add a company..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addCompany())}
                sx={{ flex: 1 }}
              />
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <Select
                  value={newCompanyTier}
                  onChange={(e) => setNewCompanyTier(e.target.value as CompanyTier)}
                  aria-label="New company tier"
                >
                  <MenuItem value="goal">Goal</MenuItem>
                  <MenuItem value="backup">Backup</MenuItem>
                  <MenuItem value="wildcard">Wild Card</MenuItem>
                </Select>
              </FormControl>
              <Button variant="outlined" size="small" onClick={addCompany}>
                Add
              </Button>
            </Box>
            {companiesByTier.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No target companies yet. Add one above, or use the Target Companies tab on
                the Front Door page for the full editor.
              </Typography>
            ) : (
              companiesByTier.map(({ tier, companies }) => (
                <Box key={tier} sx={{ mb: 1.5 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {tierGroupLabels[tier]} ({companies.length})
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {companies.map((company) => {
                      const details = [
                        company.employerQuality && `Employer quality: ${company.employerQuality}`,
                        company.listReason && `Why listed: ${company.listReason.replace(/_/g, ' ')}`,
                        company.location,
                        company.rationale,
                      ].filter(Boolean) as string[]
                      const chip = (
                        <Chip
                          label={company.name}
                          variant="outlined"
                          onDelete={() => removeCompany(company.name)}
                        />
                      )
                      return details.length > 0 ? (
                        <Tooltip key={company.name} title={details.join(' • ')} arrow>
                          {chip}
                        </Tooltip>
                      ) : (
                        <Box key={company.name}>{chip}</Box>
                      )
                    })}
                  </Box>
                </Box>
              ))
            )}
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Target Job Links
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                value={newJobLink}
                onChange={(e) => {
                  setNewJobLink(e.target.value)
                  if (jobLinkError) setJobLinkError(null)
                }}
                placeholder="Paste a job posting URL..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addJobLink())}
                error={!!jobLinkError}
                helperText={jobLinkError ?? ''}
                sx={{ flex: 1 }}
              />
              <Button variant="outlined" size="small" onClick={addJobLink}>
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.targetJobLinks.map((link) => (
                <Chip
                  key={link}
                  label={link}
                  component="a"
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  clickable
                  variant="outlined"
                  onDelete={() => removeJobLink(link)}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Compensation Section */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Compensation
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Target Salary"
              value={formData.salaryTarget}
              onChange={(e) => setFormData({ ...formData, salaryTarget: formatSalaryInput(e.target.value) })}
              placeholder="e.g., $150,000"
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              label="Minimum Acceptable Salary"
              value={formData.salaryMinimum}
              onChange={(e) => setFormData({ ...formData, salaryMinimum: formatSalaryInput(e.target.value) })}
              placeholder="e.g., $120,000"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Benefits Priorities
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {benefitOptions.map((benefit) => (
                <SelectableChip
                  key={benefit}
                  label={benefit}
                  selected={formData.benefitsPriorities.includes(benefit)}
                  onClick={() => toggleBenefit(benefit)}
                />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Work Style Section */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Work Style & Location
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preferred Work Style
            </Typography>
            <ToggleButtonGroup
              value={formData.workStyle}
              exclusive
              onChange={(_, value) => value && setFormData({ ...formData, workStyle: value })}
              fullWidth
            >
              {(Object.keys(workStyleLabels) as NonNullable<WorkStyle>[]).map((style) => (
                <ToggleButton key={style} value={style}>
                  {workStyleLabels[style]}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.willingToRelocate}
                  onChange={(e) => setFormData({ ...formData, willingToRelocate: e.target.checked })}
                />
              }
              label="Willing to Relocate"
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Location Preferences
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                value={newLocation}
                onChange={(e) => setNewLocation(e.target.value)}
                placeholder="Add a location..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addLocation())}
              />
              <Button variant="outlined" size="small" onClick={addLocation}>
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.locationPreferences.map((location) => (
                <Chip key={location} label={location} onDelete={() => removeLocation(location)} />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Priorities Section */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Priorities & Deal Breakers
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              What's Most Important (select all that apply)
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {priorityOptions.map((priority) => (
                <SelectableChip
                  key={priority}
                  label={priority}
                  selected={formData.priorities.includes(priority)}
                  onClick={() => togglePriority(priority)}
                />
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Deal Breakers
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <TextField
                size="small"
                value={newDealBreaker}
                onChange={(e) => setNewDealBreaker(e.target.value)}
                placeholder="Add a deal breaker..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addDealBreaker())}
              />
              <Button variant="outlined" size="small" onClick={addDealBreaker}>
                Add
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.dealBreakers.map((item) => (
                <Chip key={item} label={item} onDelete={() => removeDealBreaker(item)} color="error" variant="outlined" />
              ))}
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Timeline Section */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Timeline
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              fullWidth
              type="date"
              label="Target Start Date"
              value={formData.targetStartDate}
              onChange={(e) => setFormData({ ...formData, targetStartDate: e.target.value })}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>Urgency</InputLabel>
              <Select
                value={formData.urgency}
                label="Urgency"
                onChange={(e) => setFormData({ ...formData, urgency: e.target.value as Urgency })}
              >
                {(Object.keys(urgencyLabels) as Urgency[]).map((level) => (
                  <MenuItem key={level} value={level}>
                    {urgencyLabels[level]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Additional Notes */}
      <Box>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Additional Requirements
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          value={formData.additionalRequirements}
          onChange={(e) => setFormData({ ...formData, additionalRequirements: e.target.value })}
          placeholder="Any other requirements, preferences, or notes about the ideal position..."
        />
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Save / Cancel */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
        {hasSavedProfile && (
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
        )}
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving && <CircularProgress size={20} color="inherit" />}
        >
          {saving ? 'Saving...' : 'Save Dream Job Profile'}
        </Button>
      </Box>

      <DiscardChangesDialog
        open={discardOpen}
        title="Discard your changes?"
        message="Your changes to the Dream Job profile have not been saved. If you discard them, the profile goes back to how it was last saved."
        onKeepEditing={() => setDiscardOpen(false)}
        onDiscard={handleDiscard}
      />
    </Box>
  )
}
